import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import anshImg from '../assets/founders/ansh.png';
import siddhantImg from '../assets/founders/siddhant.png';
import { Navigation } from '../components/Navigation';
import { Users, Heart, Shield, Globe, Linkedin } from 'lucide-react';
import { Footer } from '../components/Footer';

export const AboutPage: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const menuItems = [
        { label: 'Features', href: '/#features' },
        { label: 'About Us', href: '/about' },
        { label: 'Blog', href: '/blog' },
        { label: 'Press', href: '/press' },
        { label: 'Pitch', href: '/pitch' },
        { label: 'Partnerships', href: '/enterprise-api' }
    ];

    // Updated scrollToSection with explicit type
    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className="min-h-screen bg-white">
            <Helmet>
                <title>About Cura – Our Mission to Democratize Healthcare</title>
                <meta name="description" content="Meet the founders building AI healthcare for everyone. Cura combines empathy with technology to deliver accessible, privacy-first medical guidance ✓" />
                <link rel="canonical" href="https://mycura.org/about" />
                <meta property="og:title" content="About Cura – Our Mission to Democratize Healthcare" />
                <meta property="og:description" content="Meet the founders building AI healthcare for everyone. Cura combines empathy with technology to deliver accessible, privacy-first medical guidance." />
                <meta property="og:url" content="https://mycura.org/about" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="About Cura – Our Mission to Democratize Healthcare" />
                <meta name="twitter:description" content="Meet the founders building AI healthcare for everyone. Cura combines empathy with technology to deliver accessible, privacy-first medical guidance." />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "AboutPage",
                        "name": "About Cura",
                        "url": "https://mycura.org/about",
                        "isPartOf": { "@type": "WebSite", "name": "Cura", "url": "https://mycura.org" }
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mycura.org/" },
                            { "@type": "ListItem", "position": 2, "name": "About", "item": "https://mycura.org/about" }
                        ]
                    })}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Person",
                        "name": "Siddhant Khapra",
                        "jobTitle": "Co-Founder & CEO",
                        "worksFor": { "@type": "Organization", "name": "Cura", "url": "https://mycura.org" },
                        "description": "Building AI-powered healthcare solutions to make medical guidance accessible for everyone."
                    })}
                </script>
            </Helmet>
            <Navigation
                menuItems={menuItems}
                scrollToSection={scrollToSection}
                scrollToTop={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            />

            <main className="pt-32 pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Hero Section */}
                    <div className="max-w-4xl mx-auto text-center mb-24">
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-6xl md:text-8xl font-['Instrument_Serif'] text-slate-900 mb-8 italic leading-[1.1]"
                        >
                            Our mission is to <br />
                            <span className="not-italic text-[#003b82]">democratize healthcare.</span>
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-xl md:text-2xl text-slate-800 leading-relaxed font-light max-w-2xl mx-auto"
                        >
                            We believe everyone deserves instant, reliable, and understandable medical guidance, regardless of age, location, or technical ability.
                        </motion.p>
                    </div>

                    {/* Founders Section - Moved to Top */}
                    <section className="mb-32">
                        <h2 className="text-4xl md:text-5xl font-['Instrument_Serif'] text-slate-900 mb-12 text-center">Our Founders</h2>
                        <div className="flex flex-col md:flex-row justify-center items-start gap-12">
                            <div className="flex flex-col items-center text-center flex-1">
                                <img src={anshImg} alt="Ansh Mehta" style={{ width: '300px', height: '300px' }} className="object-cover rounded-full mb-4" />
                                <h3 className="text-5xl font-['Instrument_Serif'] text-slate-900">Ansh Mehta</h3>
                                <strong><p className="text-lg text-slate-900">Chief Technology Officer</p></strong>
                                <p className="text-base text-slate-900 mt-4 max-w-lg mx-auto leading-relaxed">
                                    Ansh Mehta is the Chief Technology Officer of Cura and a Senior at Gretchen Whitney High School. He specializes in artificial intelligence and leads the engineering behind Cura’s core systems. With a passion for building technology that creates meaningful impact, Ansh has developed solutions across software, robotics, and data driven research. He is committed to advancing Cura’s mission by designing reliable tools that improve the way patients understand and manage their health.
                                </p>
                                <a href="https://www.linkedin.com/in/mehta-ansh/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center mt-4 text-[#0e76a8] hover:underline">
                                    <Linkedin className="w-5 h-5 mr-1" />
                                    LinkedIn
                                </a>
                            </div>
                            <div className="flex flex-col items-center text-center flex-1">
                                <img src={siddhantImg} alt="Siddhant Khapra" style={{ width: '300px', height: '300px' }} className="object-cover rounded-full mb-4" />
                                <h3 className="text-5xl font-['Instrument_Serif'] text-slate-900">Siddhant Khapra</h3>
                                <strong><p className="text-lg text-slate-900">Chief Executive Officer</p></strong>
                                <p className="text-base text-slate-900 mt-4 max-w-lg mx-auto leading-relaxed">
                                    Siddhant is currently a Senior at Gretchen Whitney High School and a founder at Cura. He guides the overall direction of Cura’s platform, drawing on experience in machine learning, data analysis, and patient-centered design. His work spans robotics, engineering research, and health technology, including two patent pending projects. Inspired by his grandmother, Siddhant created Cura to make reliable health guidance accessible through clear language, personalized insights, and intuitive tools.
                                </p>
                                <a href="https://www.linkedin.com/in/skhapra/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center mt-4 text-[#0e76a8] hover:underline">
                                    <Linkedin className="w-5 h-5 mr-1" />
                                    LinkedIn
                                </a>
                            </div>
                        </div>
                    </section>

                    {/* Values Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-32">
                        <div className="bg-[#F6F8FC] p-12 rounded-[2.5rem]">
                            <Users className="w-12 h-12 text-[#003b82] mb-6" />
                            <h3 className="text-4xl font-['Instrument_Serif'] text-slate-900 mb-4">Human-Centric AI</h3>
                            <p className="text-lg text-slate-900 leading-relaxed">
                                Technology should serve people, not the other way around. We build AI that understands natural language, empathy, and the nuances of human health.
                            </p>
                        </div>
                        <div className="bg-[#F6F8FC] p-12 rounded-[2.5rem]">
                            <Shield className="w-12 h-12 text-[#003b82] mb-6" />
                            <h3 className="text-4xl font-['Instrument_Serif'] text-slate-900 mb-4">Privacy First</h3>
                            <p className="text-lg text-slate-900 leading-relaxed">
                                Your health data is sacred. We employ state-of-the-art encryption and strict privacy protocols to ensure your information never leaves your control.
                            </p>
                        </div>
                        <div className="bg-[#F6F8FC] p-12 rounded-[2.5rem]">
                            <Globe className="w-12 h-12 text-[#003b82] mb-6" />
                            <h3 className="text-4xl font-['Instrument_Serif'] text-slate-900 mb-4">Universal Access</h3>
                            <p className="text-lg text-slate-900 leading-relaxed">
                                From WhatsApp to voice calls, we meet users where they are. No complex apps to learn, just simple, accessible healthcare for everyone.
                            </p>
                        </div>
                        <div className="bg-[#F6F8FC] p-12 rounded-[2.5rem]">
                            <Heart className="w-12 h-12 text-[#003b82] mb-6" />
                            <h3 className="text-4xl font-['Instrument_Serif'] text-slate-900 mb-4">Compassionate Care</h3>
                            <p className="text-lg text-slate-900 leading-relaxed">
                                We combine medical accuracy with the warmth of human conversation, reducing anxiety and providing clarity when it matters most.
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default AboutPage;
